import { loadImage } from './load-image';

/** Retângulo em pixels da imagem natural. */
export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Medida de uma imagem em pixels. */
export interface ImageSize {
  width: number;
  height: number;
}

/**
 * Moldura em fração de cada eixo da imagem (0–100), o formato em que a
 * `react-image-crop` a devolve como `PercentCrop`.
 */
export interface CropFraction {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Fotos de celular (ex.: 2060×3662) geram recortes enormes. O maior lado é
 * limitado a 1280 e a proporção escolhida no recorte é preservada.
 */
const MAX_OUTPUT_LONG_SIDE = 1280;
/** A mesma qualidade do editor anterior: o que o formulário grava não muda. */
const JPEG_EXPORT_QUALITY = 0.9;

function outputDimensions(width: number, height: number): ImageSize {
  const longSide = Math.max(width, height);
  if (longSide <= MAX_OUTPUT_LONG_SIDE) return { width, height };

  const scale = MAX_OUTPUT_LONG_SIDE / longSide;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * Recorta `imageSrc` na região `area`, reduz para no máximo 1280 no maior lado
 * e devolve o JPEG.
 *
 * Não gira a imagem: o giro é aplicado antes, em `rotateImageToDataUrl`, e o
 * que chega aqui já é a imagem girada.
 */
export async function getCroppedBlob(
  imageSrc: string,
  area: CropArea,
): Promise<Blob> {
  const image = await loadImage(imageSrc);
  const output = outputDimensions(area.width, area.height);

  const canvas = document.createElement('canvas');
  canvas.width = output.width;
  canvas.height = output.height;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('CANVAS_CONTEXT_UNAVAILABLE');

  ctx.drawImage(
    image,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    output.width,
    output.height,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (blob) resolve(blob);
        else reject(new Error('CANVAS_BLOB_FAILED'));
      },
      'image/jpeg',
      JPEG_EXPORT_QUALITY,
    );
  });
}

/**
 * Converte a moldura, guardada em porcentagem da imagem, para pixels do
 * arquivo original. A mesma fração vale em qualquer zoom, por isso o zoom não
 * desloca a moldura.
 */
export function toNaturalCropArea(
  fraction: CropFraction,
  natural: ImageSize,
): CropArea {
  return {
    x: Math.round((fraction.x / 100) * natural.width),
    y: Math.round((fraction.y / 100) * natural.height),
    width: Math.round((fraction.width / 100) * natural.width),
    height: Math.round((fraction.height / 100) * natural.height),
  };
}
